import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid } from "@mui/material";
import {
  Categories,
  Products,
  Actions,
  Cart,
  Checkout,
  Discount,
  Refund,
  PrintSaleReceipt,
} from "./";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useState } from "react";
import PrintReceipt from "./PrintReceipt";
import { Alert, Dialog } from "../../layout";
import { useEffect } from "react";
import CashInDrawer from "./CashInDrawer";

import { connect } from "react-redux";
import * as actions from "../../api/actions/pos";
import { endpoints } from "../../api/actions/api";

const Pos = (props) => {
  const { t } = useTranslation();

  const { _get } = props;

  const [cartItems, setCartItems] = useState([]);
  const [heldSales, setHeldSales] = useState(() => {
    try {
      const saved = localStorage.getItem("posHeldSales");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [subTotal, setSubTotal] = useState(0);
  const [totalPay, setTotalPay] = useState(0);
  const [tax, setTax] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [receiptData, setReceiptData] = useState({});
  const [drawerForm, setDrawerForm] = useState(false);
  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });
  const navigate = useNavigate();

  const handleClearCart = () => {
    setCartItems([]);
    setSubTotal(0);
    setTotalPay(0);
    setTax(0);
    setSelectedCustomer(null);
    setReceiptData({});
    navigate("/pos");
  };

  const handleHoldSale = () => {
    if (cartItems.length === 0) {
      setAlert({
        isOpen: true,
        type: "warning",
        title: t("cartEmpty"),
      });
      return;
    }

    const heldSale = {
      id: Date.now(),
      cartItems: [...cartItems],
      selectedCustomer,
      createdAt: new Date().toISOString(),
    };

    setHeldSales((prev) => [...prev, heldSale]);

    // Clear current sale without recording it as cancelled
    setCartItems([]);
    setSubTotal(0);
    setTotalPay(0);
    setTax(0);
    setSelectedCustomer(null);

    navigate("/pos");
  };
  useEffect(() => {
    // Show cash in drawer form.
    if (!("posOpen" in localStorage)) setDrawerForm(true);

    // Load customers when POS opens.
    _get(
      endpoints.Customer,
      actions.ActionType.SetCustomers,
      (res, err) => {
        if (err) {
          setAlert({
            isOpen: true,
            type: "error",
            title: t("unableToLoadCustomers"),
            subTitle: err.message || t("requestFailed"),
          });
        }
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteHeldSale = (heldSaleId) => {
    setHeldSales((prev) =>
      prev.filter((sale) => sale.id !== heldSaleId)
    );
  };

  const handleResumeSale = (heldSale) => {
    // Don't overwrite another active cart accidentally
    if (cartItems.length > 0) {
      setAlert({
        isOpen: true,
        type: "warning",
        title: t("cartNotEmpty"),
        subTitle: t("holdCurrentSaleFirst"),
      });
      return;
    }

    setCartItems(heldSale.cartItems || []);
    setSelectedCustomer(heldSale.selectedCustomer || null);

    setHeldSales((prev) =>
      prev.filter((sale) => sale.id !== heldSale.id)
    );

    navigate("/pos");
  };

  useEffect(() => {
    localStorage.setItem(
      "posHeldSales",
      JSON.stringify(heldSales)
    );
  }, [heldSales]);

  return (
    <>
      <Box className="d-none" id="printSellReceipt">
        <PrintReceipt data={receiptData} />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={7} xl={8}>
          <Box>
            {/* Body */}
            <Box
              sx={{
                height: {
                  sm: "calc(100vh - 255px)",
                  xl: "calc(100vh - 230px)",
                },
                overflow: "auto",
                borderRadius: "0.8rem",
              }}
            >
              <Routes>
                <Route
                  path="discount"
                  element={<Discount {...{ cartItems, setCartItems }} />}
                />
                <Route
                  path="refund"
                  element={
                    <Refund
                      {...{
                        cartItems,
                        setCartItems,
                        tax,
                        subTotal,
                        setAlert,
                        handleClearCart,
                        setSelectedCustomer,
                      }}
                    />
                  }
                />

                <Route
                  path="print-receipt"
                  element={
                    <PrintSaleReceipt
                      {...{
                        receiptData,
                        setReceiptData,
                        setAlert,
                      }}
                    />
                  }
                />

                <Route
                  path="checkout"
                  element={
                    <Checkout
                      {...{
                        subTotal,
                        cartItems,
                        totalPay,
                        setAlert,
                        tax,
                        setReceiptData,
                        handleClearCart,
                        selectedCustomer,
                      }}
                    />
                  }
                />
                <Route path="products">
                  <Route
                    path=":id"
                    element={
                      <Products {...{ cartItems, setCartItems, setAlert }} />
                    }
                  />
                  <Route
                    path=""
                    element={<Products {...{ cartItems, setCartItems }} />}
                  />
                </Route>
                <Route path="categories" element={<Categories />} />
                <Route
                  index
                  element={<Products {...{ cartItems, setCartItems }} />}
                />
              </Routes>
            </Box>
          </Box>
          {/* Actions */}
          <Actions
            {...{
              setReceiptData,
              setAlert,
              cartItems,
              subTotal,
              tax,
              handleClearCart,
              selectedCustomer,
              setSelectedCustomer,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={5} xl={4}>
          <Cart
            {...{
              cartItems,
              setCartItems,
              subTotal,
              setSubTotal,
              totalPay,
              setTotalPay,
              tax,
              setTax,
              selectedCustomer,
              setSelectedCustomer,
              heldSales,
              handleHoldSale,
              handleResumeSale,
              handleDeleteHeldSale,
            }}
          />
        </Grid>
      </Grid>
      {/* Edit Dialog */}
      {drawerForm && (
        <Dialog show={drawerForm} onClose={false} maxWidth="xs">
          <CashInDrawer {...{ setShow: setDrawerForm }} />
        </Dialog>
      )}
      {/* Alert  */}
      <Alert alert={alert} setAlert={setAlert} />
    </>
  );
};

const mapActionToProps = {
  _get: actions.get,
};

export default connect(null, mapActionToProps)(Pos);
